import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { createGalleryPost } from "../../features/gallery/GalleryServices";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";

import {
    IoArrowBack,
    IoCloudUploadOutline,
    IoClose,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { uploadFile } from "../../util/imageUpload";

export default function AddGalleryImage() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [templateName, setTemplateName] = useState("");
    const [mediaType, setMediaType] = useState<"IMAGE" | "VIDEO">("IMAGE");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<
        "idle" | "uploading" | "done"
    >("idle");

    const handleMediaTypeChange = (type: "IMAGE" | "VIDEO") => {
        setMediaType(type);
        setSelectedFile(null);
        setPreviewUrl(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isValid =
            mediaType === "IMAGE"
                ? file.type.startsWith("image/")
                : file.type.startsWith("video/");

        if (!isValid) {
            toast.error(
                `Please select a ${mediaType === "IMAGE" ? "image" : "video"
                } file.`
            );
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async () => {
        if (!templateName.trim()) {
            toast.error("Please enter a template name.");
            return;
        }

        if (!selectedFile) {
            toast.error(
                `Please select a ${mediaType === "IMAGE" ? "image" : "video"
                } to upload.`
            );
            return;
        }

        setSubmitting(true);

        try {
            setUploadProgress("uploading");

            const fileUrl = await uploadFile(selectedFile);

            setUploadProgress("done");

            await dispatch(
                createGalleryPost({
                    mediaUrl: fileUrl,
                    mediaType,
                    fileName: selectedFile.name,
                    templateName: templateName.trim(),
                })
            ).unwrap();

            toast.success(
                `${mediaType === "IMAGE" ? "Image" : "Video"} added successfully!`
            );

            navigate(-1);
        } catch (err: any) {
            console.error("Failed to add gallery media:", err);

            toast.error(
                err?.message ||
                `Failed to add ${mediaType === "IMAGE" ? "image" : "video"
                }.`
            );

            setUploadProgress("idle");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />

                        <span className="font-bold text-lg uppercase">
                            Add Gallery Media
                        </span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="bg-white rounded-4xl p-7 flex flex-col gap-8">

                {/* Template Name */}
                <div className="flex flex-col gap-2">
                    <label className="font-bold">Template Name</label>

                    <input
                        type="text"
                        placeholder="Enter a template name"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                    />
                </div>

                {/* Media Type */}
                <div className="flex flex-col gap-2">
                    <label className="font-bold">Media Type</label>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => handleMediaTypeChange("IMAGE")}
                            className={`px-5 py-2 rounded-lg border font-semibold transition ${mediaType === "IMAGE"
                                    ? "bg-brand-main text-white border-brand-main"
                                    : "bg-white text-gray-600 border-gray-300"
                                }`}
                        >
                            Image
                        </button>

                        <button
                            type="button"
                            onClick={() => handleMediaTypeChange("VIDEO")}
                            className={`px-5 py-2 rounded-lg border font-semibold transition ${mediaType === "VIDEO"
                                    ? "bg-brand-main text-white border-brand-main"
                                    : "bg-white text-gray-600 border-gray-300"
                                }`}
                        >
                            Video
                        </button>
                    </div>
                </div>

                {/* Media Upload */}
                <div className="flex flex-col gap-2">
                    <label className="font-bold">
                        {mediaType === "IMAGE" ? "Image" : "Video"}
                    </label>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={
                            mediaType === "IMAGE"
                                ? "image/*"
                                : "video/*"
                        }
                        className="hidden"
                        onChange={handleFileChange}
                    />

                    {!previewUrl ? (
                        <div
                            onClick={() =>
                                fileInputRef.current?.click()
                            }
                            className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 py-14 cursor-pointer hover:border-brand-main hover:bg-blue-50 transition"
                        >
                            <IoCloudUploadOutline className="text-5xl text-gray-400" />

                            <p className="text-gray-500 text-sm">
                                Click to browse or drag & drop a{" "}
                                {mediaType === "IMAGE"
                                    ? "image"
                                    : "video"}{" "}
                                here
                            </p>

                            <p className="text-gray-400 text-xs">
                                {mediaType === "IMAGE"
                                    ? "PNG, JPG, WEBP — images over 4MB will be compressed automatically"
                                    : "MP4, WEBM, MOV — select a video file"}
                            </p>
                        </div>
                    ) : (
                        <div className="relative w-full max-w-sm">
                            {mediaType === "IMAGE" ? (
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="rounded-xl object-cover w-full max-h-64 border"
                                />
                            ) : (
                                <video
                                    src={previewUrl}
                                    controls
                                    className="rounded-xl w-full max-h-64 border"
                                />
                            )}

                            <button
                                type="button"
                                onClick={handleRemoveFile}
                                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-red-50 transition"
                                title={`Remove ${mediaType === "IMAGE"
                                        ? "image"
                                        : "video"
                                    }`}
                            >
                                <IoClose className="text-red-500 text-xl" />
                            </button>

                            <p className="mt-2 text-xs text-gray-400 truncate">
                                {selectedFile?.name}
                            </p>
                        </div>
                    )}
                </div>

                {/* Upload progress */}
                {uploadProgress === "uploading" && (
                    <p className="text-sm text-blue-500 animate-pulse">
                        Uploading{" "}
                        {mediaType === "IMAGE" ? "image" : "video"},
                        please wait...
                    </p>
                )}

                {/* Submit */}
                <div className="flex sm:justify-end mt-4 sm:mt-10 w-full">
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full sm:w-auto px-6 py-2 bg-brand-blue-gradient text-white font-semibold whitespace-nowrap text-lg rounded-xl shadow hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {submitting
                            ? uploadProgress === "uploading"
                                ? `Uploading ${mediaType === "IMAGE"
                                    ? "Image"
                                    : "Video"
                                }...`
                                : "Saving..."
                            : `Add ${mediaType === "IMAGE"
                                ? "Gallery Image"
                                : "Gallery Video"
                            }`}
                    </button>
                </div>
            </div>
        </PageLayout>
    );
}